import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { EstablishmentService } from '../../../services/establishment/establishment.service';
import { RealtimeService } from '../../../services/realtime/realtime.service';
import { ToastService } from '../../../services/toast/toast.service';
import { LoaderComponent } from '../../../atoms/loader/loader.component';

// Assign accommodation: pick a property -> view its rooms with per-bed availability
// -> assign Full (all free beds) or Partial (tick free beds) -> Place. Assigned
// rooms show in the "Properties assigned" box; the progress bar tracks assigned
// beds vs total guests. Confirming happens on the bookings list, not here. SSE
// keeps the availability fresh when another user assigns beds in the same branch.
// data = { bookingId, familyCode, guestCount }.
@Component({
  selector: 'app-allocate-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, LoaderComponent],
  templateUrl: './allocate-dialog.component.html',
  styleUrl: './allocate-dialog.component.scss',
})
export class AllocateDialogComponent implements OnInit, OnDestroy {
  booking: any = null;
  guests: any[] = [];
  allocation: any[] = [];

  properties: any[] = [];
  propertySearch: string = '';

  expandedPropertyId: number | null = null;
  rooms: any[] = [];
  // roomId -> set of selected free bed numbers
  private selected = new Map<number, Set<string>>();

  isLoading: boolean = false;
  private changed: boolean = false;
  private pendingLoads: number = 0;
  private rtSub?: Subscription;

  constructor(
    public dialogRef: MatDialogRef<AllocateDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { bookingId: number; familyCode: string; guestCount: number },
    private establishmentService: EstablishmentService,
    private realtime: RealtimeService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.loadBooking();
    this.loadProperties();
    this.connectRealtime();
  }

  ngOnDestroy(): void {
    this.rtSub?.unsubscribe();
    this.realtime.close();
  }

  private connectRealtime(): void {
    const estId = localStorage.getItem('activeEstablishmentId');
    if (!estId) return;
    this.rtSub = this.realtime.connect(this.realtime.establishmentChannel(estId)).subscribe(() => {
      // another user changed allocations in this branch — refresh what we're viewing
      this.loadBooking();
      if (this.expandedPropertyId) this.loadRooms(this.expandedPropertyId);
    });
  }

  // -- loads --------------------------------------------------------------
  loadBooking(): void {
    this.beginLoad();
    this.establishmentService.getBooking(Number(this.data.bookingId)).subscribe({
      next: (res: any) => {
        this.booking = res?.data || null;
        this.guests = res?.data?.guests || [];
        this.allocation = res?.data?.allocation || [];
        this.endLoad();
      },
      error: (err) => {
        this.endLoad();
        this.toastService.error(err?.error?.message || 'Failed to load booking.');
      },
    });
  }

  loadProperties(): void {
    this.beginLoad();
    // booking-aware: utsav bookings only list properties with utsav-assigned rooms.
    this.establishmentService.getAvailableProperties(Number(this.data.bookingId)).subscribe({
      next: (res: any) => {
        this.properties = res?.data?.properties || [];
        this.endLoad();
      },
      error: (err) => {
        this.properties = [];
        this.endLoad();
        this.toastService.error(err?.error?.message || 'Failed to load properties.');
      },
    });
  }

  viewRooms(propertyId: number): void {
    if (this.expandedPropertyId === propertyId) {
      this.expandedPropertyId = null;
      this.rooms = [];
      return;
    }
    this.loadRooms(propertyId);
  }

  loadRooms(propertyId: number): void {
    this.expandedPropertyId = propertyId;
    this.beginLoad();
    this.establishmentService.getAvailableRooms(Number(this.data.bookingId), propertyId).subscribe({
      next: (res: any) => {
        this.rooms = res?.data?.rooms || [];
        this.endLoad();
      },
      error: (err) => {
        this.rooms = [];
        this.endLoad();
        this.toastService.error(err?.error?.message || 'Failed to load rooms.');
      },
    });
  }

  private beginLoad(): void {
    this.pendingLoads += 1;
    this.isLoading = true;
  }
  private endLoad(): void {
    this.pendingLoads = Math.max(0, this.pendingLoads - 1);
    this.isLoading = this.pendingLoads > 0;
  }

  // -- roster / progress --------------------------------------------------
  get maleCount(): number {
    return Number(this.booking?.maleCount ?? 0);
  }
  get femaleCount(): number {
    return Number(this.booking?.femaleCount ?? 0);
  }
  get childCount(): number {
    return Number(this.booking?.childCount ?? 0);
  }
  get totalGuests(): number {
    return Number(this.booking?.guestCount ?? this.data.guestCount ?? 0);
  }
  get placedBeds(): number {
    return this.allocation.length;
  }
  // beds ticked in the picker but not placed yet — the progress previews them.
  get selectedBeds(): number {
    let count = 0;
    this.selected.forEach((beds) => (count += beds.size));
    return count;
  }
  get projectedBeds(): number {
    return this.placedBeds + this.selectedBeds;
  }
  get placementPercent(): number {
    if (!this.totalGuests) return 0;
    return Math.min(100, Math.round((this.projectedBeds / this.totalGuests) * 100));
  }
  // width of the solid (placed) part of the bar; the ticked part fills the rest
  // of placementPercent in a lighter shade.
  get placedPercent(): number {
    if (!this.totalGuests) return 0;
    return Math.min(100, Math.round((this.placedBeds / this.totalGuests) * 100));
  }
  get remainingGuests(): number {
    return Math.max(0, this.totalGuests - this.projectedBeds);
  }

  guestName(g: any): string {
    return g?.fullName || g?.visitorName || 'Guest';
  }
  guestKind(g: any): string {
    return g?.guestType === 'covisitor' || g?.visitorId ? 'Visitor' : 'Family';
  }
  // beds are counted (not mapped per guest); show the first projectedBeds guests
  // as "Placed" so the roster previews the beds being selected too.
  guestPlaced(index: number): boolean {
    return index < this.projectedBeds;
  }

  // -- assigned box (group allocation by property) ------------------------
  get assignedGroups(): any[] {
    const byProperty = new Map<number, any>();
    for (const a of this.allocation) {
      const pid = Number(a.propertyId);
      if (!byProperty.has(pid)) {
        byProperty.set(pid, { propertyId: pid, propertyName: a.propertyName, rooms: new Map<number, any>() });
      }
      const rooms = byProperty.get(pid).rooms as Map<number, any>;
      const rid = Number(a.roomId);
      if (!rooms.has(rid)) {
        rooms.set(rid, {
          roomId: rid,
          roomNumber: a.roomNumber,
          roomType: a.roomType,
          capacity: Number(a.capacity) || 0,
          beds: [] as string[],
        });
      }
      rooms.get(rid).beds.push(a.bedNumber);
    }
    return [...byProperty.values()].map((p) => ({
      ...p,
      rooms: [...(p.rooms as Map<number, any>).values()].map((r: any) => ({
        ...r,
        full: r.capacity > 0 && r.beds.length >= r.capacity,
      })),
    }));
  }

  get assignedSummary(): { properties: number; rooms: number } {
    const groups = this.assignedGroups;
    return { properties: groups.length, rooms: groups.reduce((n, g) => n + g.rooms.length, 0) };
  }

  get filteredProperties(): any[] {
    const q = this.propertySearch.trim().toLowerCase();
    if (!q) return this.properties;
    return this.properties.filter((p: any) => String(p.name || '').toLowerCase().includes(q));
  }

  freeCount(room: any): number {
    return Number(room?.freeCount ?? (room?.beds || []).filter((b: any) => !b.taken).length);
  }
  roomStatus(room: any): string {
    const free = this.freeCount(room);
    const cap = Number(room?.capacity) || 0;
    if (free === 0) return 'Full';
    if (free === cap) return 'Free';
    return 'Partial';
  }

  // -- bed selection ------------------------------------------------------
  isSelected(roomId: number, bed: string): boolean {
    return this.selected.get(Number(roomId))?.has(bed) ?? false;
  }
  selectedCount(roomId: number): number {
    return this.selected.get(Number(roomId))?.size ?? 0;
  }
  toggleBed(room: any, bed: any): void {
    if (bed.taken) return;
    const rid = Number(room.roomId);
    if (!this.selected.has(rid)) this.selected.set(rid, new Set());
    const set = this.selected.get(rid)!;
    set.has(bed.bedNumber) ? set.delete(bed.bedNumber) : set.add(bed.bedNumber);
  }
  selectFull(room: any): void {
    const rid = Number(room.roomId);
    const set = new Set<string>((room.beds || []).filter((b: any) => !b.taken).map((b: any) => b.bedNumber));
    this.selected.set(rid, set);
  }
  clearRoom(room: any): void {
    this.selected.delete(Number(room.roomId));
  }

  // -- actions ------------------------------------------------------------
  placeRoom(room: any): void {
    const rid = Number(room.roomId);
    const bedNumbers = [...(this.selected.get(rid) ?? new Set<string>())];
    if (!bedNumbers.length) {
      this.toastService.error('Select at least one bed (or use Full).');
      return;
    }
    this.isLoading = true;
    this.establishmentService
      .allocateRooms(Number(this.data.bookingId), [{ roomId: rid, bedNumbers }])
      .subscribe({
        next: (res: any) => {
          this.toastService.success(res?.message || 'Beds assigned.');
          this.changed = true;
          this.clearRoom(room);
          this.loadBooking();
          if (this.expandedPropertyId) this.loadRooms(this.expandedPropertyId);
        },
        error: (err) => {
          this.isLoading = false;
          this.toastService.error(err?.error?.message || 'Assignment failed.');
        },
      });
  }

  release(roomId: number): void {
    this.isLoading = true;
    this.establishmentService.releaseRoom(Number(this.data.bookingId), Number(roomId)).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Room released.');
        this.changed = true;
        this.loadBooking();
        if (this.expandedPropertyId) this.loadRooms(this.expandedPropertyId);
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to release room.');
      },
    });
  }

  close(): void {
    this.dialogRef.close(this.changed);
  }
}
